import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, History } from "lucide-react";
import { fetchAdminFares, createFare, updateFare, deleteFare, fetchPriceHistory, inr, formatApiError } from "@/lib/api";

const STATUS_CHIP = {
    live: "bg-green-100 text-green-700",
    supplier_quoted: "bg-blue-100 text-blue-700",
    estimated: "bg-gold/20 text-yellow-800",
    not_available: "bg-red-100 text-red-600",
    not_priced: "bg-stone-200 text-stone-600",
};

const BLANK = { mode: "bus", origin: "", destination: "", travel_date: "", travel_class: "", passenger_category: "adult", basis: "per_person", vehicle_capacity: null, fare: null, source: "", status: "not_priced", valid_until: "", notes: "" };
const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

export function FareManager({ token }) {
    const [fares, setFares] = useState(null);
    const [labels, setLabels] = useState({});
    const [history, setHistory] = useState([]);
    const [form, setForm] = useState(null); // null = closed
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [f, h] = await Promise.all([fetchAdminFares(token), fetchPriceHistory(token)]);
            setFares(f.fares);
            setLabels(f.status_labels);
            setHistory(h.history);
        } catch { toast.error("Could not load fares"); }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.origin.trim() || !form.destination.trim()) {
            toast.error("Origin and destination are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, fare: form.fare === "" || form.fare == null ? null : Math.max(0, +form.fare), travel_date: form.travel_date || null, valid_until: form.valid_until || null, vehicle_capacity: form.vehicle_capacity || null };
            if (form.id) {
                await updateFare(token, form.id, payload);
                toast.success("Fare updated — new estimates use it immediately");
            } else {
                await createFare(token, payload);
                toast.success("Fare added");
            }
            setForm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="space-y-6">
            <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="fares-manager">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="font-display font-black text-lg text-navy">Transport Fare Quotes <span className="text-saffron">({fares?.length ?? "…"})</span></h2>
                        <p className="text-xs text-stone-500 mt-1">Customers see the status of every fare — live, supplier-quoted, estimated or not-yet-priced. Expired fares get revalidated before booking.</p>
                    </div>
                    <button onClick={() => setForm({ ...BLANK })} data-testid="fare-new-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark">
                        <Plus className="h-3.5 w-3.5" /> Add Fare
                    </button>
                </div>

                {form && (
                    <div className="mt-4 rounded-xl border border-saffron/40 bg-saffron/5 p-4" data-testid="fare-form">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className={lab}>Mode</label>
                                <select className={inp} value={form.mode} onChange={set("mode")} data-testid="fare-mode">
                                    <option value="train">Train</option><option value="flight">Flight</option><option value="bus">Bus</option><option value="private_vehicle">Private vehicle</option>
                                </select>
                            </div>
                            <div><label className={lab}>Origin *</label><input className={inp} value={form.origin} onChange={set("origin")} data-testid="fare-origin" placeholder="Mumbai" /></div>
                            <div><label className={lab}>Destination *</label><input className={inp} value={form.destination} onChange={set("destination")} data-testid="fare-destination" placeholder="Madgaon" /></div>
                            <div><label className={lab}>Class / vehicle</label><input className={inp} value={form.travel_class || ""} onChange={set("travel_class")} data-testid="fare-class" placeholder="3A / Volvo / Innova" /></div>
                            <div>
                                <label className={lab}>Basis</label>
                                <select className={inp} value={form.basis} onChange={set("basis")} data-testid="fare-basis">
                                    <option value="per_person">Per person</option><option value="per_vehicle">Per vehicle (group)</option>
                                </select>
                            </div>
                            {form.basis === "per_vehicle" && <div><label className={lab}>Vehicle capacity</label><input type="number" min="1" className={inp} value={form.vehicle_capacity ?? ""} onChange={(e) => setForm((f) => ({ ...f, vehicle_capacity: e.target.value === "" ? null : +e.target.value }))} data-testid="fare-capacity" /></div>}
                            <div><label className={lab}>Fare ₹</label><input type="number" min="0" className={inp} value={form.fare ?? ""} onChange={(e) => setForm((f) => ({ ...f, fare: e.target.value }))} data-testid="fare-amount" /></div>
                            <div>
                                <label className={lab}>Status</label>
                                <select className={inp} value={form.status} onChange={set("status")} data-testid="fare-status">
                                    <option value="live">Live / verified</option>
                                    <option value="supplier_quoted">Supplier-quoted</option>
                                    <option value="estimated">Manually estimated</option>
                                    <option value="not_available">Not available</option>
                                    <option value="not_priced">Not yet priced</option>
                                </select>
                            </div>
                            <div><label className={lab}>Source</label><input className={inp} value={form.source || ""} onChange={set("source")} data-testid="fare-source" placeholder="Operator quote #…" /></div>
                            <div><label className={lab}>Valid until</label><input type="date" className={inp} value={form.valid_until || ""} onChange={set("valid_until")} data-testid="fare-valid-until" /></div>
                            <div className="col-span-2"><label className={lab}>Notes</label><input className={inp} value={form.notes || ""} onChange={set("notes")} data-testid="fare-notes" /></div>
                        </div>
                        <div className="mt-3 flex gap-2">
                            <button onClick={save} disabled={saving} data-testid="fare-save" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-navy text-white text-xs font-display font-black uppercase tracking-wider hover:bg-navy-light disabled:opacity-60">
                                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {form.id ? "Update Fare" : "Save Fare"}
                            </button>
                            <button onClick={() => setForm(null)} data-testid="fare-cancel" className="px-6 py-2.5 rounded-full border border-stone-300 text-xs font-black uppercase tracking-wider text-stone-500 hover:border-navy hover:text-navy">Cancel</button>
                        </div>
                    </div>
                )}

                {fares === null ? (
                    <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
                ) : (
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                                    <th className="py-2 pr-4">Route</th><th className="py-2 pr-4">Mode / Class</th><th className="py-2 pr-4">Fare</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Valid until</th><th className="py-2 pr-4">Updated</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {fares.map((f) => {
                                    const expired = f.valid_until && f.valid_until < new Date().toISOString().slice(0, 10);
                                    return (
                                        <tr key={f.id} className="border-b border-stone-100" data-testid={`fare-row-${f.id}`}>
                                            <td className="py-3 pr-4 font-bold text-navy whitespace-nowrap">{f.origin} → {f.destination}</td>
                                            <td className="py-3 pr-4 whitespace-nowrap capitalize">{f.mode.replace("_", " ")}{f.travel_class ? ` · ${f.travel_class}` : ""}{f.basis === "per_vehicle" ? ` (${f.vehicle_capacity || "?"}-seater)` : ""}</td>
                                            <td className="py-3 pr-4 font-bold text-navy">{f.fare != null ? `${inr(f.fare)}${f.basis === "per_vehicle" ? "/veh" : "/pp"}` : "—"}</td>
                                            <td className="py-3 pr-4"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CHIP[f.status]}`} data-testid={`fare-status-${f.id}`}>{(labels[f.status] || f.status)}</span></td>
                                            <td className={`py-3 pr-4 whitespace-nowrap ${expired ? "text-red-600 font-bold" : "text-stone-500"}`}>{f.valid_until || "—"}{expired ? " (expired)" : ""}</td>
                                            <td className="py-3 pr-4 text-stone-400 text-xs whitespace-nowrap">{f.updated_by}<br />{new Date(f.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                                            <td className="py-3 whitespace-nowrap">
                                                <button onClick={() => setForm({ ...f, travel_date: f.travel_date || "", valid_until: f.valid_until || "", fare: f.fare ?? "" })} data-testid={`fare-edit-${f.id}`} className="text-stone-400 hover:text-navy mr-2" aria-label="Edit fare"><Pencil className="h-4 w-4" /></button>
                                                <button onClick={async () => { if (window.confirm("Delete this fare?")) { await deleteFare(token, f.id); load(); } }} data-testid={`fare-delete-${f.id}`} className="text-stone-400 hover:text-red-600" aria-label="Delete fare"><Trash2 className="h-4 w-4" /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="price-history">
                <h2 className="font-display font-black text-lg text-navy flex items-center gap-2"><History className="h-5 w-5 text-saffron" /> Price Change Audit</h2>
                <p className="text-xs text-stone-500 mt-1">Every package price and fare change, with who changed it and when.</p>
                {history.length === 0 ? (
                    <p className="py-6 text-sm text-stone-500" data-testid="price-history-empty">No price changes recorded yet.</p>
                ) : (
                    <ul className="mt-4 space-y-2 text-sm">
                        {history.slice(0, 50).map((h) => (
                            <li key={h.id} className="rounded-xl border border-stone-200 px-4 py-2.5" data-testid={`history-${h.id}`}>
                                <span className="font-bold text-navy">{h.entity_name}</span>
                                <span className="text-stone-500"> — {h.entity}/{h.field} changed by </span>
                                <span className="font-bold">{h.editor}</span>
                                <span className="text-stone-400"> · {new Date(h.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
